import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { client } from "../../networking/client";
import { LimitNotificationParam } from "../../enums/enums";
import { toast } from "react-toastify";
import { RootState } from "../store";

interface NotificationState {
    status: string;
    notifications: any[];
    notificationsHistory: any[];
    hasMore: boolean;
}

const initialState: NotificationState = {
    status: 'idle',
    notifications: [],
    notificationsHistory: [],
    hasMore: true,
}

export interface NotificationObj {
    conversation_id: string;
    payload: string;
    person_id: number;
    type: string;
}

type GetNotificationsParams = {
    limit: number
    offset: number
    person_id: number
    process?: string
    date_from?: string;
    date_to?: string;
    only_unread?: boolean
    resetState?: boolean
};

export const getNotificationsHistory = createAsyncThunk(
    'user/notification-history',
    async (params: GetNotificationsParams, { dispatch, rejectWithValue, getState }) => {
        try {
            const resetState = Boolean(params.resetState);
            delete params.resetState
            const notifications: any = await client.get(
                `/core/notifications`, {
                params
            }
            );
            return { ...notifications, resetState: resetState };
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? {});
        }
    }
);

export const markNotificationAsRead = createAsyncThunk(
    'user/mark-notification-as-read',
    async (data: { notification_ids: any[] }, { dispatch, rejectWithValue, getState }) => {
        try {
            const response = await client.put(
                `/core/notifications`, {
                ids: data.notification_ids
            }
            );
            return response;
        } catch (err: any) {
            return rejectWithValue(err?.response?.data ?? {});
        }
    }
)

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        setNotifications: (state, action) => {
            state.notifications = action.payload;
        },
        deleteNotificaionsByConversationId: (state, action) => {
            state.notifications = state.notifications.filter(
                (el) => el.conversation_id !== action.payload
            );
        },
        addNotificationItem: (state, action) => {
            state.notifications = [...state.notifications, action.payload];
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getNotificationsHistory.pending, (state) => {
            state.status = 'pending';
        }).addCase(getNotificationsHistory.fulfilled, (state, action) => {

            const handleHasMore = (notificationsNumber: number) => {
                if (notificationsNumber === LimitNotificationParam) {
                    return true
                }
                if (notificationsNumber < LimitNotificationParam) {
                    return false
                }
                return false
            }

            state.notificationsHistory = action.payload.resetState ? action.payload.data.notification_lines : [...state.notificationsHistory, ...action.payload.data.notification_lines];
            state.hasMore = handleHasMore(action.payload.data.notification_lines.length);
            state.status = 'idle';
        }).addCase(getNotificationsHistory.rejected, (state, action: any) => {
            state.status = 'rejected';
            toast.error(action?.payload?.error ?? 'Something went wrong.', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
        })
    }
})
export default notificationsSlice.reducer;

export const {
    setNotifications,
    deleteNotificaionsByConversationId,
    addNotificationItem,
} = notificationsSlice.actions;

export const selectNotifications = (state: RootState) => state.notifications.notifications;
export const selectNotificationStatus = (state: RootState) => state.notifications.status;
export const selectNotificationHistory = (state: RootState) => state.notifications.notificationsHistory;
export const selectHasMore = (state: RootState) => state.notifications.hasMore;
